import React, { useId } from 'react';
import { theme } from '../theme';

// <purpose>THE icon set (picked 2026-10-31, option 3 "action vs fact"). Two drawn languages:
//   - ACTIONS (what a clerk can do) are outlines, ~1.5 px on screen, round ends and joins;
//   - FACTS (what already happened: the retry count, Kopiert, Bearbeitet, Eingang bestätigt, the
//     decision keys, the flag shield) are solid STAMPS with the symbol cut out;
//   - TOGGLES (selection mode, active filters, the Excel lever) turn solid when on.
// Registry: design/design-languages.html, entry `icon_set`. Spec: the picked screen and its drawings
// (`icons-picked.json`, 33 keys); the three fact stamps and the Excel lever's on face were drawn and
// adopted the same day (bearbeitet, Eingang bestätigt, the approved check).</purpose>
// <constraints>
//   - Every drawing lives in a 16-unit box. The stroke is given IN SCREEN PIXELS and converted per
//     size (`strokeFor`), so a 11 px key and an 18 px nav icon draw the same line weight. Each icon's
//     screen weight is the one its picked drawing has at the size it was picked for.
//   - A stamp cuts its symbol with an SVG mask. Mask ids are document-global, so every COPY gets its
//     own id from useId — a list of stamped rows would otherwise all point at the first row's mask,
//     and one row unmounting would blank the others. `icons.test.tsx` pins it.
//   - Decorative only: aria-hidden + focusable="false" on every svg (svgA11y pins it). Meaning lives
//     in the control's name or the pill's word beside the icon.
// </constraints>

export interface IconProps { size?: number; color?: string }

/** Stroke width in box units that draws `screenPx` on screen at `size` px. */
export const strokeFor = (screenPx: number, size: number): number => Math.round((screenPx * 16 / size) * 100) / 100;

const Outline: React.FC<{ size: number; color: string; px?: number; children: React.ReactNode }> = ({ size, color, px = 1.5, children }) => (
  <svg aria-hidden="true" focusable="false" width={size} height={size} viewBox="0 0 16 16" fill="none"
    stroke={color} strokeWidth={strokeFor(px, size)} strokeLinecap="round" strokeLinejoin="round">
    {children}
  </svg>
);

/** The cut stroke inside a stamp, in box units (the picked drawings' 1.67). */
export const STAMP_CUT = 1.67;

// A useId value carries characters (":", "«", "»") that are not safe inside url(#…) everywhere.
const safeId = (raw: string): string => 'stx-' + raw.replace(/[^A-Za-z0-9_-]/g, '');

export const Stamp: React.FC<{ size: number; color: string; children: React.ReactNode }> = ({ size, color, children }) => {
  const id = safeId(useId());
  return (
    <svg aria-hidden="true" focusable="false" width={size} height={size} viewBox="0 0 16 16" fill={color} stroke="none">
      <mask id={id}>
        <rect width="16" height="16" rx="3.5" fill="white" />
        <g fill="none" stroke="black" strokeWidth={STAMP_CUT} strokeLinecap="round" strokeLinejoin="round">{children}</g>
      </mask>
      <g mask={`url(#${id})`}><rect width="16" height="16" rx="3.5" /></g>
    </svg>
  );
};

// ─── Symbols (paths shared by an outline and its stamp) ─────────────────────────────────────────
const RETRY_STAMP = <><path d="M10.8 11.48 A4.3 4.3 0 1 1 10.48 5.4" /><path d="M12.4 3.3 V6.9 H8.8" /></>;
const COPY_STAMP = <><rect x="6.6" y="6.6" width="6.4" height="6.4" rx="0.8" /><path d="M3.2 9.6 V3.2 H9.6" /></>;
const INSTEAD_STAMP = <path d="M4.2 2.8 V7.6 C4.2 9.6 5.2 10.6 7.2 10.6 H12.8 M10 7.8 L12.8 10.6 L10 13.4" />;
const CHECK_STAMP = <path d="M4 8.4 L6.8 11.1 L12 5.2" />;
const PENCIL_STAMP = <path d="M10.2 3.4 L12.6 5.8 L6.6 11.8 L3.6 12.4 L4.2 9.4Z" />;
const RECEIPT_STAMP = <path d="M3.2 4.6 H12.8 V11.4 H3.2Z M3.4 5 L8 8.4 L12.6 5" />;
const BOX = 'M1.5 2.25 H14.5 V5 H1.5Z M2.8 5 V13.75 H13.2 V5';

// ─── Actions (outline) ──────────────────────────────────────────────────────────────────────────
export const CogwheelIcon: React.FC<IconProps> = ({ size = 16, color = 'currentColor' }) => (
  <Outline size={size} color={color}>
    <path d="M15.17 7.37 L15.17 8.63 L13.34 9.68 L12.97 10.59 L13.52 12.63 L12.63 13.52 L10.59 12.97 L9.68 13.34 L8.63 15.17 L7.37 15.17 L6.32 13.34 L5.41 12.97 L3.37 13.52 L2.48 12.63 L3.03 10.59 L2.66 9.68 L0.83 8.63 L0.83 7.37 L2.66 6.32 L3.03 5.41 L2.48 3.37 L3.37 2.48 L5.41 3.03 L6.32 2.66 L7.37 0.83 L8.63 0.83 L9.68 2.66 L10.59 3.03 L12.63 2.48 L13.52 3.37 L12.97 5.41 L13.34 6.32Z" />
    <circle cx="8" cy="8" r="2.1" />
  </Outline>
);

export const ModeSwapIcon: React.FC<IconProps> = ({ size = 14, color = 'currentColor' }) => (
  <Outline size={size} color={color}><path d="M2 5 H13 M10 1.8 L13.2 5 L10 8.2 M14 11 H3 M6 7.8 L2.8 11 L6 14.2" /></Outline>
);

export const TrashIcon: React.FC<IconProps> = ({ size = 13, color = 'currentColor' }) => (
  <Outline size={size} color={color}><path d="M2 4 H14 M6 4 V2 H10 V4 M3.5 4 L4.3 14 H11.7 L12.5 4" /></Outline>
);

/** Selection mode's lever: four squares; on = two of them solid. */
export const SelectionToggleIcon: React.FC<IconProps & { active?: boolean }> = ({ size = 14, color = 'currentColor', active }) => (
  <Outline size={size} color={color}>
    <rect x="1.5" y="1.5" width="5.5" height="5.5" rx="1" fill={active ? color : 'none'} />
    <rect x="9" y="1.5" width="5.5" height="5.5" rx="1" />
    <rect x="1.5" y="9" width="5.5" height="5.5" rx="1" />
    <rect x="9" y="9" width="5.5" height="5.5" rx="1" fill={active ? color : 'none'} />
  </Outline>
);

/** Archive and restore are ONE pair: the same lidded box, the arrow going in (archive) or out. */
export const ArchiveBoxIcon: React.FC<IconProps> = ({ size = 16, color = 'currentColor' }) => (
  <Outline size={size} color={color}><path d={`${BOX} M8 6.6 V11.6 M5.9 9.5 L8 11.6 L10.1 9.5`} /></Outline>
);

/** The box with the arrow coming out: the Archiv's Auto face and every Archiv row's Wiederherstellen. */
export const RestoreIcon: React.FC<IconProps> = ({ size = 14, color = 'currentColor' }) => (
  <Outline size={size} color={color}><path d={`${BOX} M8 11.8 V6.8 M5.9 8.9 L8 6.8 L10.1 8.9`} /></Outline>
);

export const SearchIcon: React.FC<IconProps> = ({ size = 16, color = 'currentColor' }) => (
  <Outline size={size} color={color}><circle cx="7" cy="7" r="5" /><path d="M10.6 10.6 L14.5 14.5" /></Outline>
);

/** The filter lever: two sliders; on (filters active) = the knobs solid. */
export const FilterSlidersIcon: React.FC<IconProps & { active?: boolean }> = ({ size = 14, color = 'currentColor', active }) => (
  <Outline size={size} color={color}>
    <path d="M1.5 4 H8 M12 4 H14.5 M1.5 12 H4 M8 12 H14.5" />
    <circle cx="10" cy="4" r="2" fill={active ? color : 'none'} />
    <circle cx="6" cy="12" r="2" fill={active ? color : 'none'} />
  </Outline>
);

/** Two arrows; the active direction full, the other at 0.35. */
export const SortDirectionIcon: React.FC<IconProps & { dir?: 'desc' | 'asc' }> = ({ size = 14, color = 'currentColor', dir = 'desc' }) => (
  <Outline size={size} color={color}>
    <g opacity={dir === 'asc' ? 1 : 0.35}><path d="M4.5 14 V2 M1.8 4.8 L4.5 2 L7.2 4.8" /></g>
    <g opacity={dir === 'desc' ? 1 : 0.35}><path d="M11.5 2 V14 M8.8 11.2 L11.5 14 L14.2 11.2" /></g>
  </Outline>
);

export const PopoutIcon: React.FC<IconProps> = ({ size = 15, color = 'currentColor' }) => (
  <Outline size={size} color={color}><path d="M7 2.5 H2.5 V13.5 H13.5 V9 M9.5 2.5 H13.5 V6.5 M13.5 2.5 L7.5 8.5" /></Outline>
);

/** The pane's close: a plain circle on purpose — the bare cross is „Nicht hilfreich" alone. */
export const CircleIcon: React.FC<IconProps> = ({ size = 16, color = 'currentColor' }) => (
  <Outline size={size} color={color} px={1.6}><circle cx="8" cy="8" r="5.6" /></Outline>
);

export const CompareIcon: React.FC<IconProps> = ({ size = 14, color = 'currentColor' }) => (
  <Outline size={size} color={color}>
    <rect x="1.5" y="2.5" width="13" height="11" rx="1.5" />
    <path d="M8 2.5 V13.5 M4 6 H5.5 M10.5 6 H12 M4 9.5 H5.5 M10.5 9.5 H12" />
  </Outline>
);

/** The check. Outline = the Genehmigen verb; `stamp` = the approved fact (decision badge key, the decided row). */
export const CheckIcon: React.FC<IconProps & { stamp?: boolean }> = ({ size = 14, color = 'currentColor', stamp }) => (
  stamp
    ? <Stamp size={size} color={color}>{CHECK_STAMP}</Stamp>
    : <Outline size={size} color={color} px={1.6}><path d="M2.5 8.5 L6.2 12 L13.5 4" /></Outline>
);

export const CrossIcon: React.FC<IconProps> = ({ size = 14, color = 'currentColor' }) => (
  <Outline size={size} color={color} px={1.6}><path d="M3.5 3.5 L12.5 12.5 M12.5 3.5 L3.5 12.5" /></Outline>
);

export const LightningIcon: React.FC<IconProps> = ({ size = 16, color = 'currentColor' }) => (
  <Outline size={size} color={color}><path d="M9.6 1.25 L3.4 9.1 H7.7 L6.4 14.75 L12.6 6.9 H8.3 Z" /></Outline>
);

/** The retry ring. Outline = Neu analysieren; `stamp` = the retry COUNT (a fact on the row). */
export const RetryIcon: React.FC<IconProps & { stamp?: boolean }> = ({ size = 14, color = 'currentColor', stamp }) => (
  stamp
    ? <Stamp size={size} color={color}>{RETRY_STAMP}</Stamp>
    : <Outline size={size} color={color}><path d="M11.86 12.15 A5.6 5.6 0 1 1 11.45 4.24" /><path d="M13.6 2 V6.3 H9.3" /></Outline>
);

/** Copy to clipboard (the UUID). `done` = the copied fact: the Kopiert stamp. */
export const CopySmallIcon: React.FC<IconProps & { done?: boolean }> = ({ size = 11, color = 'currentColor', done }) => (
  done
    ? <Stamp size={size} color={color}>{COPY_STAMP}</Stamp>
    : <Outline size={size} color={color}><rect x="5.5" y="5.5" width="9" height="9" rx="1.5" /><path d="M2.5 10.5 V2.5 H10.5" /></Outline>
);

export const UploadMailIcon: React.FC<IconProps> = ({ size = 24, color = 'currentColor' }) => (
  <Outline size={size} color={color} px={2}>
    <path d="M8 4 H1.5 V14 H14.5 V8.5 M1.5 4.5 L8 9 L10 7.6" /><path d="M12.5 6 V1 M10.6 2.9 L12.5 1 L14.4 2.9" />
  </Outline>
);

export const PaperclipIcon: React.FC<IconProps> = ({ size = 13, color = theme.colors.navyMuted }) => (
  <Outline size={size} color={color}><path d="M11 5.5 V10.5 A3 3 0 0 1 5 10.5 V4.5 A1.75 1.75 0 0 1 8.5 4.5 V10" transform="rotate(40 8 8)" /></Outline>
);

/** Bearbeiten. Under 15 px the bare pencil; from 15 px it writes on its line. */
export const PencilIcon: React.FC<IconProps> = ({ size = 15, color = 'currentColor' }) => (
  size >= 15
    ? <Outline size={size} color={color}><path d="M10.5 2.5 L13.5 5.5 L6 13 L2.5 13.5 L3 10Z M2.5 15 H13.5" /></Outline>
    : <Outline size={size} color={color}><path d="M10.5 2 L14 5.5 L6 13.5 L2 14 L2.5 10Z" /></Outline>
);

/** The form-to-Excel lever: a sheet; on = its header row solid. */
export const SpreadsheetRowIcon: React.FC<IconProps & { active?: boolean }> = ({ size = 14, color = 'currentColor', active }) => (
  <Outline size={size} color={color}>
    <rect x="1.5" y="2.5" width="13" height="11" rx="1.5" />
    {active && <path d="M1.5 2.5 H14.5 V6.2 H1.5Z" fill={color} />}
    <path d="M1.5 6.2 H14.5 M1.5 9.8 H14.5 M6 6.2 V13.5" />
  </Outline>
);

/** Download the picked rows: one arrow onto a line. */
export const DownloadArrowIcon: React.FC<IconProps> = ({ size = 16, color = 'currentColor' }) => (
  <Outline size={size} color={color}><path d="M8 1.75 V10.25 M4.6 6.9 L8 10.25 L11.4 6.9 M2.25 13.75 H13.75" /></Outline>
);

/** Download everything in view: three arrows onto the line — bounded vs unbounded, told apart before the click. */
export const MassDownloadIcon: React.FC<IconProps> = ({ size = 16, color = 'currentColor' }) => (
  <Outline size={size} color={color}>
    <path d="M3.75 1.75 V8.25 M2 6.5 L3.75 8.25 L5.5 6.5 M8 1.75 V9.75 M6.25 8 L8 9.75 L9.75 8 M12.25 1.75 V8.25 M10.5 6.5 L12.25 8.25 L14 6.5 M1.75 13.75 H14.25" />
  </Outline>
);

// ─── Facts (stamps) ─────────────────────────────────────────────────────────────────────────────
/** „Kopiert": the trace key. */
export const CopyIcon: React.FC<IconProps> = ({ size = 11, color = 'currentColor' }) => <Stamp size={size} color={color}>{COPY_STAMP}</Stamp>;

/** The declined-instead decision key: „the clerk did THIS instead". */
export const InsteadIcon: React.FC<IconProps> = ({ size = 11, color = 'currentColor' }) => <Stamp size={size} color={color}>{INSTEAD_STAMP}</Stamp>;

/** „Eingang bestätigt": a receipt went out to the sender. */
export const ReceiptIcon: React.FC<IconProps> = ({ size = 11, color = 'currentColor' }) => <Stamp size={size} color={color}>{RECEIPT_STAMP}</Stamp>;

/** „bearbeitet": the clerk edited the letter (the Bearbeiten ACTION keeps the outline PencilIcon). */
export const PencilStampIcon: React.FC<IconProps> = ({ size = 11, color = 'currentColor' }) => <Stamp size={size} color={color}>{PENCIL_STAMP}</Stamp>;

/** The flag shield beside a flag word: solid, the bar cut out (even-odd, no mask needed). */
export const ShieldIcon: React.FC<IconProps> = ({ size = 12, color = 'currentColor' }) => (
  <svg aria-hidden="true" focusable="false" width={size} height={size} viewBox="0 0 16 16" fill={color} stroke="none">
    <path fillRule="evenodd" d="M8 0.6 L14.6 2.9 V7.6 C14.6 11.6 11.9 14.3 8 15.5 C4.1 14.3 1.4 11.6 1.4 7.6 V2.9Z M4.8 6.8 H11.2 V9 H4.8Z" />
  </svg>
);

export const DownloadIcon: React.FC<{ size?: number; color?: string }> = ({ size = 14, color = 'currentColor' }) => (
  <svg aria-hidden="true" focusable="false" width={size} height={size} viewBox="0 0 24 24" fill="none"
    stroke={color} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" />
    <polyline points="7 10 12 15 17 10" /><line x1="12" y1="15" x2="12" y2="3" />
  </svg>
);

export const ChevronDownIcon: React.FC<{ size?: number; color?: string }> = ({ size = 14, color = 'currentColor' }) => (
  <svg aria-hidden="true" focusable="false" width={size} height={size} viewBox="0 0 24 24" fill="none"
    stroke={color} strokeWidth="2.4" strokeLinecap="round" strokeLinejoin="round">
    <polyline points="6 9 12 15 18 9" />
  </svg>
);

// Why: design dept v2 (~/Documents/xlsx2.svg) — larger text, evenodd letter cutouts, recolored white-on-navy
export const ExcelIcon: React.FC<{ size?: number }> = ({ size = 30 }) => (
  <svg aria-hidden="true" focusable="false" width={size} height={size} viewBox="-2 0 354 354" style={{ fillRule: 'evenodd' as const, clipRule: 'evenodd' as const }}>
    <path d="m 65.384767,0 c -6.793,0 -12.30859,5.514594 -12.3086,12.308594 v 85.042972 l 185.796883,0.58203 c 6.79297,0.0213 12.30858,5.514594 12.30859,12.308594 v 110.44922 c 0,6.794 -5.51559,12.31055 -12.30859,12.31055 H 53.076167 v 108.95898 c 0,6.794 5.5156,12.31055 12.3086,12.31055 H 286.95703 c 6.793,0 12.3086,-5.51655 12.3086,-12.31055 V 81.998046 c -35.84776,-0.0294 -41.36682,-0.24219 -73.62696,-0.24219 -11.57089,0 -10.46484,0.98995 -10.46484,-10.07226 V 0 Z" fill="#ffffff"/>
    <path d="m 219.0918,0 -0.0273,75.162106 c 0,1.64466 0.40056,2.02149 1.96484,2.02149 l 78.04297,-0.0293 z" fill="rgba(255,255,255,0.6)"/>
    <path d="m 0,102.60743 v 113.3457 c 0,6.93022 5.515604,12.55664 12.308594,12.55664 H 233.88086 c 6.793,0 12.3086,-5.62642 12.3086,-12.55664 V 115.91211 c -1e-5,-6.93023 -5.51563,-12.53494 -12.3086,-12.55664 z m 93.773437,24.85351 c 2.784,0 4.79883,2.01678 4.79883,4.80078 v 52.41602 c 0,4.8 2.016783,8.16015 4.800783,8.16015 h 2.40039 c 2.496,0 4.32031,2.01483 4.32031,4.79883 0,2.784 -2.68929,4.80078 -6.52929,4.80078 h -0.19141 c -8.448003,0 -14.400393,-7.39176 -14.400393,-17.75976 v -52.41602 c 0,-2.784 2.01678,-4.80078 4.80078,-4.80078 z M 138.125,149.54102 c 8.448,0 15.16782,2.87998 19.00782,7.58398 1.728,2.112 1.63123,4.51283 -0.38477,6.04883 -2.112,1.44 -5.27922,0.76736 -7.19922,-1.05664 -3.264,-3.072 -6.71923,-4.03125 -11.61523,-4.03125 -5.664,0 -10.08008,2.01606 -10.08008,5.66406 0,3.552 2.01481,5.66339 12.38281,7.77539 14.4,2.88 19.20117,7.96874 19.20117,15.55274 0,8.736 -7.10462,15.74414 -20.64062,15.74414 -8.928,0 -17.0885,-3.07288 -21.3125,-7.29688 -1.92,-2.016 -1.53613,-4.99075 0.67187,-6.71875 2.496,-2.016 5.18382,-1.53718 7.00782,0.38282 2.208,2.4 7.39204,4.99218 13.24804,4.99218 7.392,0 11.42549,-2.68734 11.52149,-6.52734 0.096,-3.84 -3.07264,-6.62522 -13.05664,-8.44922 -12.96,-2.496 -18.43164,-7.77597 -18.43164,-15.16797 0,-9.792 9.88768,-14.49609 19.67968,-14.49609 z m -101.472653,0.38477 c 1.44,0 2.68843,0.48081 3.64843,1.63281 l 15.74414,18.81445 14.11133,-18.81445 c 0.96,-1.248 2.11274,-1.63281 3.55274,-1.63281 4.224,0 5.3752,4.60875 2.7832,7.96875 l -14.49609,18.9121 15.26562,18.23829 c 2.88,3.36 0.86344,7.39257 -2.97656,7.39257 -1.344,0 -2.49703,-0.28715 -3.45703,-1.53515 l -15.26367,-18.24024 -14.4961,18.24024 c -0.96,1.056 -2.11108,1.53515 -3.45508,1.53515 -3.936,0 -5.47315,-4.22398 -2.78515,-7.58398 l 14.88086,-18.62305 -15.83985,-18.7207 c -2.592,-3.072 -1.53679,-7.58398 2.78321,-7.58398 z m 134.400393,0 c 1.44,0 2.68844,0.48081 3.64843,1.63281 l 15.74415,18.81445 14.11132,-18.81445 c 0.96,-1.248 2.11274,-1.63281 3.55274,-1.63281 4.224,0 5.3752,4.60875 2.7832,7.96875 l -14.49609,18.9121 15.26367,18.23829 c 2.88,3.36 0.86539,7.39257 -2.97461,7.39257 -1.344,0 -2.49703,-0.28715 -3.45703,-1.53515 l -15.26367,-18.24024 -14.4961,18.24024 c -0.96,1.056 -2.11108,1.53515 -3.45508,1.53515 -3.936,0 -5.47315,-4.22398 -2.78515,-7.58398 l 14.88086,-18.62305 -15.83985,-18.7207 c -2.592,-3.072 -1.53679,-7.58398 2.78321,-7.58398 z" fill="#ffffff" style={{ strokeWidth: 1.00998 }}/>
  </svg>
);

// Why: design dept v2 (~/Documents/pdf2.svg) — larger text, evenodd letter cutouts + counter circles for p/d
export const PdfPageIcon: React.FC<{ size?: number }> = ({ size = 30 }) => (
  <svg aria-hidden="true" focusable="false" width={size} height={size} viewBox="-2 0 354 354" style={{ fillRule: 'evenodd' as const, clipRule: 'evenodd' as const }}>
    <path d="m 65.695314,0 c -6.793,0 -12.30859,5.514594 -12.3086,12.308594 v 85.042972 l 185.796876,0.58203 c 6.79297,0.0213 12.30858,5.514594 12.30859,12.308594 v 110.44922 c 0,6.794 -5.51559,12.31055 -12.30859,12.31055 H 53.386714 v 108.95898 c 0,6.794 5.5156,12.31055 12.3086,12.31055 H 287.26757 c 6.793,0 12.3086,-5.51655 12.3086,-12.31055 V 81.998046 c -35.84776,-0.0294 -41.36682,-0.24219 -73.62696,-0.24219 -11.57089,0 -10.46484,0.98995 -10.46484,-10.07226 V 0 Z" fill="#ffffff"/>
    <path d="m 219.40234,0 -0.0273,75.162106 c 0,1.64466 0.40056,2.02149 1.96484,2.02149 l 78.04297,-0.0293 z" fill="rgba(255,255,255,0.6)"/>
    <path d="m 137.59375,148.29102 c -9.696,0 -17.08789,7.67917 -17.08789,17.95117 0,10.272 7.39189,18.04883 17.08789,18.04883 9.696,0 16.99218,-7.77683 16.99218,-18.04883 0,-10.272 -7.29618,-17.95117 -16.99218,-17.95117 z" fill="#ffffff" style={{ strokeWidth: 1.00998 }}/>
    <path d="m 72.697264,148.29102 c -9.696,0 -16.99219,7.77487 -16.99219,18.04687 0,10.272 7.29619,17.95313 16.99219,17.95313 9.696,0 17.08789,-7.68113 17.08789,-17.95313 0,-10.272 -7.39189,-18.04687 -17.08789,-18.04687 z" fill="#ffffff" style={{ strokeWidth: 1.00998 }}/>
    <path d="m 0,102.50391 v 113.3457 c 0,6.93022 5.515596,12.55664 12.308594,12.55664 H 233.88086 c 6.793,0 12.30859,-5.62642 12.30859,-12.55664 V 115.8086 c -1e-5,-6.93023 -5.51562,-12.53494 -12.30859,-12.55664 z m 159.09765,15.06641 c 2.88,0 4.89649,2.01648 4.89649,4.89648 v 43.77539 c -0.096,15.168 -11.42439,26.68945 -26.40039,26.68945 -15.072,0 -26.4961,-11.52145 -26.4961,-26.68945 0,-15.168 10.56017,-26.5918 24.57617,-26.5918 7.584,0 14.11135,3.36033 18.52735,8.73633 V 122.4668 c 0,-2.88 2.01648,-4.89648 4.89648,-4.89648 z m 44.83203,0 c 2.784,0 4.89649,1.92001 4.89649,4.41601 0,2.496 -2.11249,4.41602 -4.89649,4.41602 -7.2,0 -11.90429,5.18494 -11.90429,12.96094 v 2.59179 h 9.79297 c 2.688,0 4.51171,1.72666 4.51171,4.22266 0,2.304 -1.82371,4.0332 -4.51171,4.0332 h -9.79297 v 37.43945 c 0,2.97601 -1.91989,4.89649 -5.08789,4.89649 -2.496,0 -4.70313,-1.92049 -4.70313,-4.89649 v -37.43945 h -4.12891 c -2.688,0 -4.51171,-1.7292 -4.51171,-4.0332 0,-2.496 1.82371,-4.22266 4.51171,-4.22266 h 4.12891 v -1.63281 c 0,-13.728 8.83131,-22.75195 21.69531,-22.75195 z M 72.697264,139.65039 c 15.072,1e-5 26.4961,11.5195 26.4961,26.6875 0,15.168 -10.56018,26.59375 -24.57618,26.59375 -7.584,0 -14.11134,-3.36032 -18.52734,-8.73632 v 25.91992 c 0,2.88 -2.01648,4.89453 -4.89648,4.89453 -2.88,0 -4.89649,-2.01453 -4.89649,-4.89453 v -43.77735 c 0.096,-15.168 11.42439,-26.6875 26.40039,-26.6875 z" fill="#ffffff" style={{ strokeWidth: 1.00998 }}/>
  </svg>
);

export const PinIcon: React.FC<{ size?: number; color?: string; filled?: boolean }> = ({ size = 14, color = 'currentColor', filled }) => (
  <svg aria-hidden="true" focusable="false" width={size} height={size} viewBox="0 0 24 24"
    fill={filled ? color : 'none'}
    stroke={color} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <line x1="12" y1="17" x2="12" y2="22" />
    <path d="M5 17h14v-1.76a2 2 0 0 0-1.11-1.79l-1.78-.9A2 2 0 0 1 15 10.76V6h1a2 2 0 0 0 0-4H8a2 2 0 0 0 0 4h1v4.76a2 2 0 0 1-1.11 1.79l-1.78.9A2 2 0 0 0 5 15.24Z" />
  </svg>
);

export const ThumbUpIcon: React.FC<{ size?: number; color?: string }> = ({ size = 14, color = 'currentColor' }) => (
  <svg aria-hidden="true" focusable="false" width={size} height={size} viewBox="0 0 24 24" fill="none"
    stroke={color} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M14 9V5a3 3 0 0 0-3-3l-4 9v11h11.28a2 2 0 0 0 2-1.7l1.38-9a2 2 0 0 0-2-2.3zM7 22H4a2 2 0 0 1-2-2v-7a2 2 0 0 1 2-2h3" />
  </svg>
);

// ─── The handling glyphs (wim vocabulary v7, 2026-09-13) ─────────────────────────────────────
// SIX drawn icons, one per handling the wim vocabulary serves an `icon` name for. They are the
// strip's face in strip_abbr mode: a 32px colour column has room for a glyph or for two letters,
// and a glyph says "forward this" to a reader who has not learnt the Kürzel yet.
// Drawn, never typed: an emoji or a dingbat would be a product-emitted decorative character (house
// rule), platform-dependent in weight and baseline, and untouchable by a stroke width.
// Same contract as every other icon in this file — decorative (svgA11y pins aria-hidden +
// focusable="false"; the strip's meaning lives in its tooltip), 24-unit viewBox, stroke 2, round
// caps and joins, `color` from the caller (the strip passes white onto its own hue).
export const ForwardIcon: React.FC<{ size?: number; color?: string }> = ({ size = 16, color = 'currentColor' }) => (
  <svg aria-hidden="true" focusable="false" width={size} height={size} viewBox="0 0 24 24" fill="none"
    stroke={color} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M4 12h12" /><path d="M12 6l6 6-6 6" />
  </svg>
);

export const ReplyIcon: React.FC<{ size?: number; color?: string }> = ({ size = 16, color = 'currentColor' }) => (
  <svg aria-hidden="true" focusable="false" width={size} height={size} viewBox="0 0 24 24" fill="none"
    stroke={color} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M9 6l-6 6 6 6" /><path d="M3 12h10a7 7 0 0 1 7 7" />
  </svg>
);

export const LookupIcon: React.FC<{ size?: number; color?: string }> = ({ size = 16, color = 'currentColor' }) => (
  <svg aria-hidden="true" focusable="false" width={size} height={size} viewBox="0 0 24 24" fill="none"
    stroke={color} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <circle cx="11" cy="11" r="6" /><path d="M20 20l-4.5-4.5" />
  </svg>
);

export const ArchiveIcon: React.FC<{ size?: number; color?: string }> = ({ size = 16, color = 'currentColor' }) => (
  <svg aria-hidden="true" focusable="false" width={size} height={size} viewBox="0 0 24 24" fill="none"
    stroke={color} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <rect x="3" y="4" width="18" height="4" /><path d="M5 8v11h14V8" /><path d="M10 12h4" />
  </svg>
);

export const QuestionIcon: React.FC<{ size?: number; color?: string }> = ({ size = 16, color = 'currentColor' }) => (
  <svg aria-hidden="true" focusable="false" width={size} height={size} viewBox="0 0 24 24" fill="none"
    stroke={color} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M9 9a3 3 0 1 1 4.5 2.6c-.9.5-1.5 1.2-1.5 2.4" /><path d="M12 18h.01" />
  </svg>
);

// The sixth glyph (part 5-2): the unclassified bucket's face — a dashed, unfilled circle ("nothing
// assigned"). Same drawn contract as the five above.
export const VoidIcon: React.FC<{ size?: number; color?: string }> = ({ size = 16, color = 'currentColor' }) => (
  <svg aria-hidden="true" focusable="false" width={size} height={size} viewBox="0 0 24 24" fill="none"
    stroke={color} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <circle cx="12" cy="12" r="8" strokeDasharray="3 3" />
  </svg>
);

// THE agreed set, and the only mapping from a served `icon` name to a drawn glyph. The vocabulary
// serves a NAME (categoryMeta.resolveIcon deliberately returns no markup), so this table is where a
// name becomes a component — and a name outside it resolves to nothing at all, which is what lets
// HandlingStripFace fall back to the Kürzel instead of drawing a placeholder box for a glyph the
// bundle does not have.
export type HandlingIconName = 'forward' | 'reply' | 'lookup' | 'archive' | 'question' | 'void';

export const HANDLING_ICONS: Record<HandlingIconName, React.FC<{ size?: number; color?: string }>> = {
  forward: ForwardIcon,
  reply: ReplyIcon,
  lookup: LookupIcon,
  archive: ArchiveIcon,
  question: QuestionIcon,
  void: VoidIcon,
};
